import React, { useContext } from 'react';
import {
  StyledTabButton,
  StyledRequiredMark,
  ScreenReaderOnly,
  StyledSubTabButton,
  StyledSelectionIndicator,
} from './styles.js';
import { DSTabsContext, DSTabsCrossRefContext } from '../../DSTabsCTX.js';
import { useKeyboardNavigation } from '../../utils/hooks/useKeyboardNavigation.js';
import { centerTab } from '../../utils/helpers.js';
import type { DSTabT } from '../../react-desc-prop-types.js';
import { TABS_DATA_TESTID, TAB_TYPES } from '../../constants/index.js';

export const TabBarItemRenderer: React.ComponentType<Record<never, never>> = React.memo(() => {
  const {
    tabsRef,
    focusableTabsRef,
    tabsRefAsArray,
    carouselOnlyListRef,
    actualActiveTab,
    props: { type, fixedTabsHeaders, withCarousel, isDSMobile, showSeparator, children: tabs },
    globalClickHandler,
  } = useContext(DSTabsContext);

  const { lastTabInternalRef, firstSubtabInternalRef } = useContext(DSTabsCrossRefContext);
  const { handleOnKeyDown } = useKeyboardNavigation();

  const availableTabIndexes: number[] = [];

  return React.Children.map(tabs, (tab: React.ReactElement<DSTabT.Props>, index) => {
    const {
      tabId = '',
      title = '',
      style,
      required = false,
      disabled = false,
      applyAriaDisabled = false,
      ref,
      onClick,
      onKeyDown,
      ...rest
    } = tab.props;
    const getOwnerProps = () => tab.props;
    const getOwnerPropsArguments = () => ({ tabId });

    if (!disabled) availableTabIndexes.push(index);
    const ButtonComponent = type === TAB_TYPES.SUBTABS ? StyledSubTabButton : StyledTabButton;

    const isActive = actualActiveTab === tabId;

    return (
      <ButtonComponent
        {...rest}
        getOwnerProps={getOwnerProps}
        getOwnerPropsArguments={getOwnerPropsArguments}
        key={tabId}
        role="tab"
        // eslint-disable-next-line complexity
        innerRef={(tabButtonRef: HTMLButtonElement) => {
          if (tabButtonRef && tabsRefAsArray.current && focusableTabsRef.current && tabsRef.current) {
            tabsRefAsArray.current[index] = tabButtonRef;
            if (!disabled && !focusableTabsRef.current.includes(tabButtonRef))
              focusableTabsRef.current.push(tabButtonRef);
            tabsRef.current[index] = tabButtonRef;
            if (type === TAB_TYPES.SUBTABS && firstSubtabInternalRef && index === availableTabIndexes[0]) {
              firstSubtabInternalRef.current = tabButtonRef;
            }
            if (type !== TAB_TYPES.SUBTABS && index === availableTabIndexes[availableTabIndexes.length - 1]) {
              lastTabInternalRef.current = tabButtonRef;
            }
            if (ref) ref.current = tabButtonRef;
          }
        }}
        type="button"
        aria-controls={tabId}
        aria-selected={actualActiveTab === tabId && !applyAriaDisabled}
        aria-disabled={disabled || applyAriaDisabled}
        isDSMobile={isDSMobile}
        fixedTabsHeaders={fixedTabsHeaders}
        tabType={type}
        data-required={required}
        data-tab-id={tabId}
        data-index={index}
        withCarousel={withCarousel}
        isActive={isActive}
        disabled={disabled}
        showSeparator={showSeparator && type !== TAB_TYPES.NORMAL}
        data-testid={type === TAB_TYPES.NORMAL ? TABS_DATA_TESTID.TAB_BUTTON : TABS_DATA_TESTID.SUBTAB_BUTTON}
        id={`${tabId}-label`}
        onClick={(e) => {
          /** [PUI-15772] - Tabs Pre Refactor
           * Click handler for tab buttons. It:
           * 1. Prevents tab change if aria-disabled is true
           * 2. Updates active tab and indicator style if not disabled
           * 3. Calls the user's onClick callback if provided
           */
          if (applyAriaDisabled) return;
          if (!disabled) {
            globalClickHandler({ event: e, target: 'tabItem', tabId });
          }
          if (onClick && !disabled) onClick(tabId, e);
        }}
        onFocus={(e) => {
          /** [PUI-15772] - Tabs Pre Refactor
           * Focus handler for carousel tabs. It:
           * 1. Centers the focused tab in the carousel viewport
           * 2. Only applies when withCarousel prop is true
           */
          if (withCarousel) centerTab({ e, listRef: carouselOnlyListRef });
        }}
        onKeyDown={(e) => {
          /** [PUI-15772] - Tabs Pre Refactor
           * Keyboard navigation handler. It:
           * 1. Prevents Enter key propagation
           * 2. Handles keyboard navigation if not disabled
           * 3. Calls the user's onKeyDown callback if provided
           */
          if (e.key === 'Enter') e.stopPropagation();
          if (!disabled) handleOnKeyDown(e);
          if (onKeyDown && !disabled) onKeyDown(e);
        }}
        style={style}
      >
        {title}
        {required && (
          <>
            <StyledRequiredMark aria-hidden="true">&#9679;</StyledRequiredMark>
            <ScreenReaderOnly>Required</ScreenReaderOnly>
          </>
        )}
        {isActive && type !== TAB_TYPES.SUBTABS ? (
          <StyledSelectionIndicator aria-hidden="true" isDSMobile={isDSMobile} tabType={type} />
        ) : null}
      </ButtonComponent>
    );
  });
});
